import React, { useEffect, useRef, useState } from 'react';
import styles from './PicklistEdit.module.scss';

import BreadcrumbsNav from 'components/BreadcrumbsNav/BreadcrumbsNav';
import MainButton from 'components/StyledComponents/MainButton';
import FormInputText from 'components/FormComponents/FormInputText/FormInputText';
import FormSearchInput from 'components/FormComponents/FormSearchInput/FormSearchInput';
import ConfirmationPopup from 'components/ConfirmationPopup/ConfirmationPopup';
import BackButton from 'components/BackButton/BackButton';

import { useForm, FormProvider } from 'react-hook-form';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { useCommonActions, useCommonSelector } from 'hooks/Common';
import { usePicklistActions, usePicklistDetailsActions, usePicklistSelector } from 'hooks/Picklist';
import { useHistory, useParams } from 'react-router-dom';

import { getBackendErrors } from 'helpers/AppHelpers';
import { getErrorsProperties, isParameterInvalid } from 'helpers/ErrorValidator';

import {
  CREATE_NEW_PICKLIST,
  EXIT_WITHOUT_SAVING,
  NEW_PICKLIST_TYPE,
  UPDATE_PICKLIST,
  UPDATE_PICKLIST_TYPE,
  WITHOUT_SAVING_BY_NAV_TYPE,
  WITHOUT_SAVING_TYPE
} from 'constants/dialogPopupsData';
import { PICKLIST_DETAILED_PATH, PICKLIST_SUMMARY_PATH } from 'constants/routeConstants';

export default function PicklistEdit() {
  const isMobile = useMobileViewport();
  const { id } = useParams();
  const history = useHistory();
  const routeUrl = useRef(null);

  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Create Picklist' }]);
  const [dialogModalData, setDialogModalData] = useState({});

  const { unsavedFormData } = useCommonSelector();
  const { setUnsavedFormDataAction } = useCommonActions();
  const { creationForm } = usePicklistSelector();
  const { getCreationFormAction, createPicklistAction, updatePicklistAction, clearStateAction } =
    usePicklistActions();
  const { getPicklistAction } = usePicklistDetailsActions();

  const { projects, locations } = creationForm;

  const methods = useForm({
    defaultValues: {
      name: '',
      pocName: '',
      pocNumber: '',
      location: {},
      sublocation: null,
      project: {}
    },
    mode: 'onChange'
  });
  const { setValue, getValues, formState, reset, clearErrors, setError, watch } = methods;
  const { isDirty, errors } = formState;
  const isFormInvalid = !!Object.values(errors).filter(Boolean).length;

  const changeInputValue = (name, value) => setValue(name, value, { shouldDirty: true });

  useEffect(() => {
    const queries = {
      project: { includeUserCheck: true },
      location: { filters: { active: true, includeUserCheck: true } }
    };
    getCreationFormAction(queries);
  }, []);

  useEffect(() => {
    if (isDirty && !unsavedFormData) {
      setUnsavedFormDataAction(true);
    }
    return () => {
      setUnsavedFormDataAction(false);
    };
  }, [isDirty]);

  useEffect(() => {
    if (id) {
      getPicklistAction(id).then((res) => {
        reset(res);
        setBreadcrumbs([
          { path: `${PICKLIST_DETAILED_PATH}/${id}`, name: res.name },
          { name: 'Edit Picklist' }
        ]);
      });
    }
  }, [id]);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const onInput = (key, value) => {
    errors[key]?.message && validateForm(key);
    changeInputValue(key, value);
  };

  const selectLocation = (name, value) => {
    changeInputValue(name, value);
    changeInputValue('pocName', value?.pocName || '');
    changeInputValue('pocNumber', value?.pocNumber || '');
    errors?.location?.message && validateForm(name);
  };

  const selectSearchOption = (name, value) => changeInputValue(name, value);

  const selectProject = (name, value) => {
    changeInputValue(name, value);
    changeInputValue('location', {});
    changeInputValue('sublocation', null);
    errors?.project?.message && validateForm(name);
    errors?.location?.message && validateForm('location');
  };

  const errorProperties = [
    { name: 'name', inputType: 'text', errorMessage: 'Name field is required' },
    { name: 'project', inputType: 'object', errorMessage: 'Project Number field is required' },
    { name: 'location', inputType: 'object', errorMessage: 'Location field is required' }
  ];

  const validateForm = (key) => {
    if (key) {
      clearErrors(key);
      return;
    }
    let isFormValid = true;
    errorProperties.forEach(({ name, inputType, errorMessage }) => {
      if (isParameterInvalid(getValues()[name], inputType)) {
        setError(name, getErrorsProperties(errorMessage));
        isFormValid = false;
      }
    });
    return isFormValid;
  };

  const handleErrors = (err) => {
    getBackendErrors(err).forEach(({ name, type, message }) => setError(name, { type, message }));
  };

  const closeModal = () => setDialogModalData({ isOpened: false });
  const agreeModal = () => {
    switch (dialogModalData.type) {
      case NEW_PICKLIST_TYPE:
        closeModal();
        createPicklistAction(getValues()).then((err) => err?.errors && handleErrors(err));
        break;
      case UPDATE_PICKLIST_TYPE:
        closeModal();
        updatePicklistAction(getValues(), {
          shouldRedirectToDetails: false,
          shouldDisplayActionPopup: true
        }).then((err) => err?.errors && handleErrors(err));
        break;
      case WITHOUT_SAVING_TYPE:
        getBack();
        break;
      case WITHOUT_SAVING_BY_NAV_TYPE:
        history.push(routeUrl.current);
        break;
      default:
        break;
    }
  };

  const getBack = () =>
    id ? history.push(`${PICKLIST_DETAILED_PATH}/${id}`) : history.push(PICKLIST_SUMMARY_PATH);

  const onCancel = () => {
    if (isDirty) {
      setDialogModalData({ ...EXIT_WITHOUT_SAVING, isOpened: true });
    } else {
      getBack();
    }
  };

  const onCreate = () => {
    if (!validateForm()) return;

    const MODAL_DATA = id ? UPDATE_PICKLIST : CREATE_NEW_PICKLIST;
    setDialogModalData({ ...MODAL_DATA, isOpened: true });
  };

  const getSublocations = () => {
    if (!getValues()?.location?.id) return [];

    const sublocations =
      locations?.find(({ id }) => id === getValues().location.id)?.sublocations || [];
    const areSublocationsExist = !!sublocations?.length;

    return areSublocationsExist ? sublocations : [];
  };

  const filterLocationsByProjectId = (options) => {
    if (!getValues()?.project?.id || !options?.length) return [];
    return options.filter(({ projectId }) => projectId === getValues().project.id);
  };
  const enrichLocationOptions = () => {
    if (
      locations?.length &&
      getValues()?.location?.id &&
      !locations.map(({ id }) => id).includes(getValues().location.id)
    ) {
      return locations.concat({ ...getValues()?.location }).sort((a, b) => a.id > b.id);
    } else return locations;
  };

  const setSelectedRoute = (path) => (routeUrl.current = path);

  const projectWatcher = watch('project');
  const locationWatcher = watch('location');

  return (
    <section className={styles.pageContainer}>
      <ConfirmationPopup data={dialogModalData} onAgree={agreeModal} onDismiss={closeModal} />
      <div>
        {!isMobile && (
          <BreadcrumbsNav
            itemsArray={breadcrumbs}
            setDialogModalData={setDialogModalData}
            formIsChanged={isDirty}
            setRouteUrl={setSelectedRoute}
          />
        )}
        <div className={styles.headerBlock}>
          <h1>{id ? 'Edit' : 'Create'} Picklist</h1>
          {isMobile && <BackButton onCancel={onCancel} />}
        </div>
        <FormProvider {...methods}>
          <section className={styles.formWrapper}>
            <section className={styles.formRow}>
              <label>Name*</label>
              <FormInputText name="name" onChange={onInput} max={40} />
            </section>
            <section className={styles.formRow}>
              <label>Project Number*</label>
              <FormSearchInput name="project" options={projects || []} onSelect={selectProject} />
            </section>
            <section className={styles.formRow}>
              <label>Location*</label>
              <FormSearchInput
                name="location"
                options={filterLocationsByProjectId(enrichLocationOptions()) || []}
                onSelect={selectLocation}
                isDisabled={!projectWatcher?.id}
              />
            </section>
            <section className={styles.formRow}>
              <label>Sublocation</label>
              <FormSearchInput
                clearable
                name="sublocation"
                options={getSublocations() || []}
                onSelect={selectSearchOption}
                isDisabled={!locationWatcher?.id}
              />
            </section>
            <section className={styles.formRow}>
              <label>POC Name</label>
              <FormInputText name="pocName" onChange={onInput} max={40} />
            </section>
            <section className={styles.formRow}>
              <label>POC Number</label>
              <FormInputText name="pocNumber" onChange={onInput} max={40} />
            </section>
          </section>
        </FormProvider>
      </div>
      <div className={styles.footerControls}>
        <MainButton text="cancel" type="secondary" action={onCancel} />
        <MainButton
          text={id ? 'save' : 'create'}
          type="primary"
          action={onCreate}
          isDisabled={isFormInvalid}
        />
      </div>
    </section>
  );
}
